"use client";

import type { Session } from "@supabase/supabase-js";
import { useRouter } from "next/navigation";
import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { createClient } from "../supabase/client";

export interface SessionContext {
	session: Session | null;
	isUserLoading: boolean;
	error: string | null;
}

const SessionContext = createContext<SessionContext | undefined>(undefined);

interface SessionProviderProps {
	initialSession: Session | null;
	children: React.ReactNode;
}

export function SessionProvider({
	initialSession,
	children,
}: SessionProviderProps) {
	const router = useRouter();
	const supabase = useMemo(() => createClient(), []);

	const [session, setSession] = useState<Session | null>(
		initialSession || null,
	);
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		let mounted = true;

		async function getSession() {
			const {
				data: { session },
				error,
			} = await supabase.auth.getSession();

			// only update the react state if the component is still mounted
			if (mounted) {
				if (error) {
					setError(error.message);
					setIsLoading(false);
					return;
				}

				setSession(session);
				setIsLoading(false);
			}
		}

		getSession();

		return () => {
			mounted = false;
		};
	}, [supabase]);

	useEffect(() => {
		const {
			data: { subscription },
		} = supabase.auth.onAuthStateChange((event, session) => {
			if (event === "SIGNED_OUT") {
				setSession(null);
			} else if (event === "PASSWORD_RECOVERY") {
				router.push("/auth/update-password");
			} else if (session) {
				setSession(session);
			}
		});

		return () => {
			subscription.unsubscribe();
		};
	}, [supabase, router]);

	const value: SessionContext = useMemo(() => {
		if (isLoading) {
			return {
				session: null,
				isUserLoading: true,
				error: null,
			};
		}

		if (error) {
			return {
				session: null,
				isUserLoading: false,
				error,
			};
		}

		return {
			session,
			isUserLoading: false,
			error: null,
		};
	}, [isLoading, session, error]);

	return (
		<SessionContext.Provider value={value}>{children}</SessionContext.Provider>
	);
}

export function useUser() {
	const context = useContext(SessionContext);
	if (context === undefined) {
		console.error(
			"SessionContext is undefined. Make sure the component is wrapped in SessionProvider.",
		);
		return {
			user: null,
			isUserLoading: false,
			error: new Error("SessionContext not available"),
		};
	}
	return {
		user: context.session?.user || null,
		isUserLoading: context.isUserLoading,
		error: context.error,
	};
}

export function useSession() {
	const context = useContext(SessionContext);
	if (context === undefined) {
		console.error(
			"SessionContext is undefined. Make sure the component is wrapped in SessionProvider.",
		);
		return {
			session: null,
			isUserLoading: false,
			error: new Error("SessionContext not available"),
		};
	}
	return {
		session: context.session,
		isUserLoading: context.isUserLoading,
		error: context.error,
	};
}
